import React from "react";
import { Check, Edit2, CreditCard, MailWarning, Send, KeyRound, Loader2 } from "lucide-react";
import Roles from "@/shared/valueObjects/Roles";
import GlassContainer from "@/shared/components/glasscontainer";

interface AccountCardProps {
  id: string;
  name: string;
  email: string;
  role: Roles;
  /** Cuenta ya confirmada desde el enlace del correo. */
  emailConfirmed?: boolean;
  /** Hay un correo en curso para esta cuenta. */
  sendingMail?: boolean;
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  onEdit?: () => void;
  onViewPlan?: () => void;
  onResendConfirmation?: () => void;
  onSendPasswordReset?: () => void;
}

export const AccountCard: React.FC<AccountCardProps> = ({
  name,
  email,
  role,
  emailConfirmed = true,
  sendingMail = false,
  selected = false,
  onSelect,
  onEdit,
  onViewPlan,
  onResendConfirmation,
  onSendPasswordReset,
}) => {
  const avatarText = (name || email).substring(0, 2).toUpperCase();
  const avatarBg = "#3b82f6"; // Default color, could be generated from name

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.();
  };

  const handleViewPlanClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewPlan?.();
  };

  const handleMailAction = (e: React.MouseEvent, action?: () => void) => {
    e.stopPropagation();
    if (!sendingMail) action?.();
  };

  const getRoleLabel = (r: any) => {
    if (Roles.isSuperAdmin(r)) return "SuperAdmin";
    if (Roles.isAdmin(r)) return "Admin";
    if (Roles.isCuenta(r)) return "Cuenta";
    return "Usuario";
  };

  return (
    <GlassContainer
      onClick={() => onSelect?.(!selected)}
      className={`group relative cursor-pointer rounded-3xl transition-all duration-500 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] overflow-hidden
        ${selected
          ? "border-accent bg-white/80 shadow-[0_20px_40px_-12px_rgba(0,113,227,0.2)] -translate-y-1 scale-[1.02]"
          : " hover:border-white hover:bg-glass-shine hover:-translate-y-1.5 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.12)]"
        }`}
    >
      <div className="p-4 relative flex flex-col min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="flex size-12 shrink-0 items-center justify-center rounded-xl font-bold text-[14px] shadow-[inset_0_1px_2px_rgba(255,255,255,0.6),0_4px_12px_rgba(0,0,0,0.06)] transition-transform group-hover:scale-110 duration-500"
            style={{ backgroundColor: `${avatarBg}15`, color: avatarBg }}
          >
            {avatarText}
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-[15px] font-bold text-foreground/90 tracking-tight group-hover:text-accent transition-colors">
              {name || "Sin nombre"}
            </h3>
            <p className="text-[12px] text-gray font-medium opacity-80 truncate">
              {email}
            </p>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 min-w-0">
            <span
              className={`text-[10px] font-bold px-2.5 py-1 rounded-full border transition-colors shrink-0 ${Roles.isSuperAdmin(role)
                  ? "bg-purple-500/10 text-purple-600 border-purple-500/10"
                  : Roles.isAdmin(role)
                    ? "bg-blue-500/10 text-blue-600 border-blue-500/10"
                    : "bg-gray/10 text-gray border-black/5"
                }`}
            >
              {getRoleLabel(role).toUpperCase()}
            </span>

            {/* Indicador solo cuando la cuenta todavía no confirmó el correo. */}
            {!emailConfirmed && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full border shrink-0 bg-amber-500/10 text-amber-600 border-amber-500/20"
                title="La cuenta no confirmó su correo todavía"
              >
                <MailWarning className="size-3" />
                SIN CONFIRMAR
              </span>
            )}
          </span>

          <div className="flex items-center gap-1.5 shrink-0">
            {!emailConfirmed ? (
              <button
                onClick={(e) => handleMailAction(e, onResendConfirmation)}
                disabled={sendingMail}
                className="size-7 cursor-pointer rounded-full border border-black/5 bg-white/40 flex items-center justify-center hover:bg-white hover:text-amber-600 transition-all opacity-0 group-hover:opacity-100 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Reenviar correo de confirmación"
              >
                {sendingMail ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Send className="size-3.5" />
                )}
              </button>
            ) : (
              <button
                onClick={(e) => handleMailAction(e, onSendPasswordReset)}
                disabled={sendingMail}
                className="size-7 cursor-pointer rounded-full border border-black/5 bg-white/40 flex items-center justify-center hover:bg-white hover:text-accent transition-all opacity-0 group-hover:opacity-100 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Enviar correo de cambio de contraseña"
              >
                {sendingMail ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <KeyRound className="size-3.5" />
                )}
              </button>
            )}
            <button
              onClick={handleViewPlanClick}
              className="size-7 cursor-pointer rounded-full border border-black/5 bg-white/40 flex items-center justify-center hover:bg-white hover:text-accent transition-all opacity-0 group-hover:opacity-100"
              title="Ver plan"
            >
              <CreditCard className="size-3.5" />
            </button>
            <button
              onClick={handleEditClick}
              className="size-7 cursor-pointer rounded-full border border-black/5 bg-white/40 flex items-center justify-center hover:bg-white hover:text-accent transition-all opacity-0 group-hover:opacity-100"
              title="Editar"
            >
              <Edit2 className="size-3.5" />
            </button>
            <div
              className={`size-7 rounded-full border transition-all duration-300 flex items-center justify-center
              ${selected ? "bg-accent border-accent scale-110 shadow-[0_0_15px_rgba(0,113,227,0.4)]" : "border-black/10 bg-white/40 group-hover:border-black/20"}`}
            >
              {selected && <Check className="size-3.5 text-white stroke-4" />}
            </div>
          </div>
        </div>
      </div>
    </GlassContainer>
  );
};
